/**
 * Storage Helper Utilities
 *
 * Safe wrappers for sessionStorage and localStorage operations
 * to handle QuotaExceededError and other storage access issues
 * (e.g., Safari private browsing, storage disabled)
 */

import { logger } from './logger';

/**
 * Check if a storage type is available and accessible
 */
export function isStorageAvailable(type: 'localStorage' | 'sessionStorage'): boolean {
  try {
    const storage = window[type];
    if (!storage) {
      return false;
    }

    // Test both read and write operations
    const testKey = '__storage_test__';
    storage.setItem(testKey, 'test');
    const result = storage.getItem(testKey);
    storage.removeItem(testKey);

    return result === 'test';
  } catch (error) {
    // QuotaExceededError, SecurityError, or any other error
    return false;
  }
}

/**
 * Safely get item from sessionStorage with fallback
 */
export function safeSessionStorageGetItem(key: string, defaultValue: string | null = null): string | null {
  try {
    if (!isStorageAvailable('sessionStorage')) {
      return defaultValue;
    }
    return sessionStorage.getItem(key) || defaultValue;
  } catch (error) {
    // Log only in development to avoid noise in production
    if (process.env.NODE_ENV === 'development') {
      logger.warn(`Failed to read from sessionStorage (key: ${key}):`, error);
    }
    return defaultValue;
  }
}

/**
 * Safely set item in sessionStorage
 * Returns true if successful, false otherwise
 */
export function safeSessionStorageSetItem(key: string, value: string): boolean {
  try {
    if (!isStorageAvailable('sessionStorage')) {
      return false;
    }
    sessionStorage.setItem(key, value);
    return true;
  } catch (error) {
    // Log only in development to avoid noise in production
    if (process.env.NODE_ENV === 'development') {
      logger.warn(`Failed to write to sessionStorage (key: ${key}):`, error);
    }
    return false;
  }
}

/**
 * Safely parse JSON from sessionStorage with fallback
 */
export function safeSessionStorageGetJSON<T = any>(key: string, defaultValue: T): T {
  const value = safeSessionStorageGetItem(key);
  if (!value) {
    return defaultValue;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      logger.warn(`Failed to parse JSON from sessionStorage (key: ${key}):`, error);
    }
    return defaultValue;
  }
}

/**
 * Safely store JSON in sessionStorage
 * Returns true if successful, false otherwise
 */
export function safeSessionStorageSetJSON(key: string, value: any): boolean {
  try {
    const jsonString = JSON.stringify(value);
    return safeSessionStorageSetItem(key, jsonString);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      logger.warn(`Failed to stringify JSON for sessionStorage (key: ${key}):`, error);
    }
    return false;
  }
}

/**
 * Safely get item from localStorage with fallback
 */
export function safeLocalStorageGetItem(key: string, defaultValue: string | null = null): string | null {
  try {
    if (!isStorageAvailable('localStorage')) {
      return defaultValue;
    }
    return localStorage.getItem(key) || defaultValue;
  } catch (error) {
    // Log only in development to avoid noise in production
    if (process.env.NODE_ENV === 'development') {
      logger.warn(`Failed to read from localStorage (key: ${key}):`, error);
    }
    return defaultValue;
  }
}

/**
 * Safely set item in localStorage
 * Returns true if successful, false otherwise
 */
export function safeLocalStorageSetItem(key: string, value: string): boolean {
  try {
    if (!isStorageAvailable('localStorage')) {
      return false;
    }
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    // Log only in development to avoid noise in production
    if (process.env.NODE_ENV === 'development') {
      logger.warn(`Failed to write to localStorage (key: ${key}):`, error);
    }
    return false;
  }
}

/**
 * Safely parse JSON from localStorage with fallback
 */
export function safeLocalStorageGetJSON<T = any>(key: string, defaultValue: T): T {
  const value = safeLocalStorageGetItem(key);
  if (!value) {
    return defaultValue;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      logger.warn(`Failed to parse JSON from localStorage (key: ${key}):`, error);
    }
    return defaultValue;
  }
}

/**
 * Safely store JSON in localStorage
 * Returns true if successful, false otherwise
 */
export function safeLocalStorageSetJSON(key: string, value: any): boolean {
  try {
    const jsonString = JSON.stringify(value);
    return safeLocalStorageSetItem(key, jsonString);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      logger.warn(`Failed to stringify JSON for localStorage (key: ${key}):`, error);
    }
    return false;
  }
}

/**
 * Safely remove item from localStorage
 */
export function safeLocalStorageRemoveItem(key: string): boolean {
  try {
    if (!isStorageAvailable('localStorage')) {
      return false;
    }
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      logger.warn(`Failed to remove from localStorage (key: ${key}):`, error);
    }
    return false;
  }
}